import React, {useState} from 'react';
import {flowRight as compose} from 'lodash';
import {withTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {LineChart, XAxis, Tooltip, YAxis, Line, ResponsiveContainer} from 'recharts';
import {RANGE_CHART} from '../../../common/constants';
import SelectRangeChart from './SelectRangeChart';
import styled from 'styled-components';
import FlexRow from '../../../common/components/layout/FlexRow';
import MonthPicker from '../../../common/components/Dates/MonthPicker';
import YearPicker from '../../../common/components/Dates/YearPicker';
import gql from 'graphql-tag';
import {useQuery} from '@apollo/react-hooks';
import {PulseLoader} from 'react-spinners';
import Error from '../../../common/components/Error/Error';
import {Alert} from 'reactstrap';
import {displayTimeUnit} from '../utils';


const FlexRowDashboard = styled(FlexRow)`
  margin-bottom: 33px;
`;

const MargedDiv = styled.div`
  margin-left: 33px;
`;

const SmallMargedDiv = styled.div`
  margin-left: 8px;
`;


const GRAPHQL_QUERY_MERCHANT_STATISTICS = gql`
  query merchantStatistics($statisticsInput: StatisticsInput!) {
    merchantStatistics(statisticsInput: $statisticsInput){
      id
      times
      receipts
      merchantUsers {
        id
        totalUsers
        totalUsersWithLoyaltyCard
      }
    }
  }
`;

const ChartDashboardAdmin = ({t, lineColor}) => {

  const [rangeChart, setRangeChart] = useState(RANGE_CHART.MONTH);
  const [month, setMonth] = useState((new Date().getMonth() + 1));
  const [year, setYear] = useState((new Date().getFullYear()));

  const {loading, error, data} = useQuery(GRAPHQL_QUERY_MERCHANT_STATISTICS, {
    variables: {
      statisticsInput: {
        year,
        month,
        rangeChart,
      },
    },
  });

  if (loading) {
    return <PulseLoader/>;
  } else if (error) {
    return <Error error={t('errorOnGraph')}/>;
  } else {
    return (
      <>
        <FlexRowDashboard>
          <SelectRangeChart value={rangeChart} onChange={e => setRangeChart(e.target.value)}/>
          {rangeChart === RANGE_CHART.DAY && (
            <MargedDiv>
              <MonthPicker value={month} onChange={e => setMonth(+e.target.value)}/>
            </MargedDiv>
          )}
          {(rangeChart === RANGE_CHART.DAY || rangeChart === RANGE_CHART.MONTH) && (
            <SmallMargedDiv>
              <YearPicker value={year} onChange={e => setYear(+e.target.value)}/>
            </SmallMargedDiv>
          )}
        </FlexRowDashboard>
        {data.merchantStatistics.length === 0 ? (
          <Alert color="info">{t('noDataForPeriod')}</Alert>
        ) : (
          <ResponsiveContainer
            width={'100%'}
            minHeight={'300px'}
            maxHeight={'50vh'}
          >
            <LineChart
              data={data.merchantStatistics}
              margin={{top: 10, right: 10, left: 10, bottom: 10}}
            >
              <XAxis
                dataKey="times"
                tickLine={false}
                tickFormatter={tick => displayTimeUnit(tick, rangeChart)}
              />
              <YAxis
                yAxisId="receipts"
                tickLine={false}
                stroke={lineColor.tickets}
              />
              <YAxis
                yAxisId="totalUsers"
                orientation="right"
                tickLine={false}
                stroke={lineColor.totalUsers}
              />
              <Tooltip
                labelFormatter={tick => displayTimeUnit(tick, rangeChart)}
              />
              <Line
                yAxisId="receipts"
                type="monotone"
                name={t('tickets')}
                dataKey="receipts"
                stroke={lineColor.tickets}
                activeDot={{r: 7}}
              />
              <Line
                yAxisId="totalUsers"
                type="monotone"
                name={t('reachableClients')}
                dataKey="merchantUsers.totalUsers"
                stroke={lineColor.totalUsers}
                activeDot={{r: 7}}
              />
              <Line
                yAxisId="totalUsers"
                type="monotone"
                name={t('usersWithLoyaltyCard')}
                dataKey="merchantUsers.totalUsersWithLoyaltyCard"
                stroke={lineColor.totalUsersWithLoyaltyCard}
                activeDot={{r: 7}}
              />
            </LineChart>
          </ResponsiveContainer>
        )
        }
      </>
    );
  }
};

ChartDashboardAdmin.defaultProps = {
  lineColor:{
    totalUsers: '#20a8d8',
    totalUsersWithLoyaltyCard: '#63c2de',
    tickets: '#f86c6b',
  },
};

ChartDashboardAdmin.propTypes = {
  t: PropTypes.func.isRequired,
  lineColor: PropTypes.shape({
    totalUsers: PropTypes.string,
    totalUsersWithLoyaltyCard: PropTypes.string,
    tickets: PropTypes.string,
  }),
};

export default compose(
  withTranslation('dashboard'),
)(ChartDashboardAdmin);
